import type { GetServerSideProps } from "next";
import { useRouter } from "next/router";
import { useCallback, useEffect } from "react";
import { useCookies } from "react-cookie";
import { useInterval } from "react-use";

import { GoogleTag, trackEvent } from "../../components/external/GoogleTag";
import ShortSignupForm from "../../components/users/SignUp/ShortSignupForm";
import SignUpForm from "../../components/users/SignUp/SignUpForm";
import SignUpLayout from "../../components/users/SignUp/SignUpLayout";
import type { SignUpMutation } from "../../lib/graphql";
import { useCurrentUser } from "../../lib/hooks/basic/users";
import { hardRedirect } from "../../lib/redirect";
import routes from "../../lib/routes";
import { isSignUpEnabled } from "../../lib/utils";

export default function SignUp() {
  const router = useRouter();
  const { user, forceTenantOnboarding, refetch } = useCurrentUser();
  const { token, tenant, plan, period, accountState, returnTo } = router.query;
  const [cookies] = useCookies();

  const trackConversion = useCallback(
    (result: SignUpMutation["signUp"]) => {
      trackEvent({
        action: "sign_up",
        params: {
          plan: plan || "free",
          period,
          user_id: result?.user?.id,
        },
      });
    },
    [plan, period]
  );

  useEffect(() => {
    if (user?.id) {
      if (accountState === "creating") {
        // if farm is not created yet, show creating state in form
        if (forceTenantOnboarding) return;

        return hardRedirect(routes.onboarding + "?pillars=true");
      }

      return hardRedirect((returnTo as string) || routes.dashboard);
    }
  }, [user?.id, accountState, forceTenantOnboarding, returnTo]);

  useInterval(() => {
    if (user?.id && accountState === "creating") refetch();
  }, 5000);

  const shortForm = !plan || plan === "free";

  // if not in creating flow, render nothing
  if (user?.id && accountState !== "creating") return;

  const countryCode = cookies["country_code"];

  return (
    <>
      <SignUpLayout>
        {shortForm ? (
          <ShortSignupForm
            token={token as string}
            plan={plan as string}
            period={period as string}
            tenantName={tenant as string}
            countryCode={countryCode}
            onSuccess={trackConversion}
          />
        ) : (
          <SignUpForm
            token={token as string}
            plan={plan as string}
            period={period as string}
            tenantName={tenant as string}
            countryCode={countryCode}
            onSuccess={trackConversion}
          />
        )}
      </SignUpLayout>

      {!token && <GoogleTag />}
    </>
  );
}

export const getServerSideProps: GetServerSideProps = async () => {
  return isSignUpEnabled() ? { props: {} } : { notFound: true };
};

SignUp.skipAuth = true;
SignUp.getLayout = (page: React.ReactElement) => page;
